import React, { useRef, useState } from 'react';
import { Typography, Box, FormGroup, FormControlLabel, Checkbox } from '@mui/material';
import { useTranslation } from 'services/localization/useTranslation';
import ActionDialog from 'common/components/ActionDialog';

interface Props {
	open: boolean;
	skuCount: number;
	articlesCount: number;
	onClose: () => void;
	handleDelete: (opts: { deleteArticles: boolean }) => void;
	loading: boolean;
}

const DeleteSkuDialog = (props: Props) => {
	const { t } = useTranslation();
	const { open, skuCount, articlesCount, handleDelete, onClose, loading } = props;
	const count = useRef(skuCount);
	const [deleteArticles, setDeleteArticles] = useState<boolean>(false);

	return open ? (
		<ActionDialog
			title={t('deleteSkuDialog.deleteSkus', 'Delete SKUs?')}
			headerVariant="contained"
			btnActions={{
				primary: {
					text: t('common:actions.yesDelete', 'Yes, delete'),
					onClick: () => handleDelete({ deleteArticles }),
					loading,
					disabled: loading,
				},
				secondary: {
					text: t('common:actions.no', 'No'),
					onClick: onClose,
				},
			}}
			onClose={onClose}
			variant="danger"
		>
			<>
				<Box>
					<Typography>
						{t('deleteSkuDialog.deleteConfirm', {
							skuCount: count.current,
							defaultValue: 'Are you sure you want to delete the selected {{skuCount}} SKUs',
						})}
						?
					</Typography>
				</Box>
				{articlesCount > 0 && (
					<Box mt={3}>
						<Typography>
							{t('deleteSkuDialog.deleteArticlesConfirm', {
								articlesCount,
								defaultValue:
									'This will also affect {{articlesCount}} of your articles that are connected to these SKUs - they will no longer have an SKU. You will need to assign new SKUs for these articles.',
							})}
						</Typography>

						<FormGroup sx={{ mt: 1 }}>
							<FormControlLabel
								control={
									<Checkbox
										checked={deleteArticles}
										onChange={(e) => setDeleteArticles(e.target.checked)}
									/>
								}
								label={t(
									'deleteSkuDialog.deleteArticles',
									'Delete also the articles connected to these SKUs',
								)}
							/>
						</FormGroup>
					</Box>
				)}
				<Box mt={3}>
					<Typography>
						{t('deleteSkuDialog.deleteInfo', 'This action cannot be undone')}.
					</Typography>
				</Box>
			</>
		</ActionDialog>
	) : null;
};

export default DeleteSkuDialog;
